import { useState } from 'react';
import { GraduationCap, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTrainingModeStore } from '@/store/trainingMode';
import { usePermissions } from '@/hooks/usePermissions';

interface TrainingModeSwitchProps {
  /** Whether the user has items in their current order */
  hasItemsInCart: boolean;
  /** Callback to clear the current order when entering training mode */
  onClearOrder: () => void;
}

/**
 * Dropdown button that appears in the POS header.
 * Allows switching between live POS and Training Mode.
 * Permission-gated: only shown to users with pos.training_mode.
 */
export function TrainingModeSwitch({ hasItemsInCart, onClearOrder }: TrainingModeSwitchProps) {
  const { isTrainingMode, enterTrainingMode, exitTrainingMode } = useTrainingModeStore();
  const { hasPermission } = usePermissions();
  const [showEnterDialog, setShowEnterDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showInProgressWarning, setShowInProgressWarning] = useState(false);

  // Only render if user has the training_mode permission
  if (!hasPermission('pos.training_mode')) return null;

  const handleEnterClick = () => {
    if (hasItemsInCart) {
      setShowInProgressWarning(true);
    } else {
      setShowEnterDialog(true);
    }
  };

  const handleConfirmEnter = () => {
    onClearOrder();
    enterTrainingMode();
    setShowEnterDialog(false);
    setShowInProgressWarning(false);
  };

  const handleConfirmExit = () => {
    exitTrainingMode();
    onClearOrder();
    setShowExitDialog(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={isTrainingMode ? 'default' : 'outline'}
            size="sm"
            className={
              isTrainingMode
                ? 'gap-1.5 bg-amber-500 hover:bg-amber-600 text-white border-amber-600'
                : 'gap-1.5'
            }
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
            Switch
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {isTrainingMode ? (
            <DropdownMenuItem
              onClick={() => setShowExitDialog(true)}
              className="gap-2 cursor-pointer"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Exit Training Mode
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={handleEnterClick}
              className="gap-2 cursor-pointer"
            >
              <GraduationCap className="h-4 w-4" />
              Switch to Training Mode
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation dialog: entering training mode (no sale in progress) */}
      <AlertDialog open={showEnterDialog} onOpenChange={setShowEnterDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <GraduationCap className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-center">Switch to Training Mode?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              You are about to enter Training Mode. While active, sales and payments
              will <span className="font-semibold text-foreground">not be recorded</span>.
              All training data is discarded when you exit. Do not process any real sales
              while in Training Mode.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEnter}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Got it, enter Training Mode
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Warning dialog: sale in progress */}
      <AlertDialog open={showInProgressWarning} onOpenChange={setShowInProgressWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sale in Progress</AlertDialogTitle>
            <AlertDialogDescription>
              Switching to Training Mode will discard your current sale. You can go back
              to complete the sale first, or continue and discard it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Return to Sale in Progress</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEnter}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Discard and Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation dialog: exiting training mode */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">Exit Training Mode?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              You will return to the live POS where sales and payments{' '}
              <span className="font-semibold text-foreground">will be recorded</span>.
              All training data will be discarded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel>Continue Training</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmExit}>
              Exit Training Mode
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
